import type { Submission, SubmissionStatus } from './types.ts';
import { docDisplayStatusLabel } from './submissionStatus.ts';
import { hasExpiredLiveFile } from './documentVersions.ts';

// The status shown on every question in the post-submit vendor view. Superset of
// SubmissionStatus with an explicit "not_answered" so an unanswered question
// (e.g. one the admin added after submission) always shows a status, plus
// "expired" for an approved document whose expiry has lapsed (see
// effectiveDocStatus — display-only, both render "Not Compliant").
export type QuestionDisplayStatus = SubmissionStatus | 'not_answered' | 'expired';

/** Whether the vendor has put any content against this submission. */
export function submissionHasAnswer(submission: Submission | undefined): boolean {
  if (!submission) return false;
  return (
    submission.answer !== null ||
    submission.submission_files.length > 0 ||
    submission.submission_adoptions.length > 0
  );
}

/**
 * Display status for a question in the post-submit view. A reviewer verdict
 * (approved/rejected/not_applicable) always wins; otherwise the question is
 * Pending if it has an answer, or Not answered if it does not. An approved
 * document that has since expired shows as "expired" (Not Compliant).
 */
export function questionDisplayStatus(
  submission: Submission | undefined,
  now: Date = new Date(),
): QuestionDisplayStatus {
  if (!submission) return 'not_answered';
  const status = submission.status;
  if (status === 'approved') {
    return hasExpiredLiveFile(submission.submission_files, now) ? 'expired' : 'approved';
  }
  if (status === 'rejected' || status === 'not_applicable') {
    return status;
  }
  return submissionHasAnswer(submission) ? 'pending' : 'not_answered';
}

export function questionDisplayStatusLabel(status: QuestionDisplayStatus): string {
  // Every non-"not_answered" value is a DocDisplayStatus, so reuse that single
  // source of truth (also used by the admin doc badge) to avoid label drift.
  return status === 'not_answered' ? 'Not answered' : docDisplayStatusLabel(status);
}
